import React, { useContext, useState, useMemo } from 'react';
import { Box, Typography, Grid, Modal, Paper, Link } from '@mui/material';
import { DataAndFilterContext } from '../../context/DataAndFilterContext';
import CustomerModal from '../CustomerModal';
import { computeInsightScore } from '../../utils/scoring';

const ROW_FIELD = 'insight_product_area';
const COL_FIELD = 'insight_sentiment_overall'; // e.g., Positive / Negative / Wish List / etc.

/** Collect insights in one cell, returning average sentiment, occurrences, score, and the items */
const collectAvgCountScore = (data, rowValue, colValue) => {
  if (!Array.isArray(data)) return { average: null, count: 0, score: 0, insights: [] };

  const matching = data.flatMap((file) => {
    const jobInsights = (file.jobs_to_be_done || []).flatMap((job) =>
      (job.insights || [])
        .filter((i) => i[ROW_FIELD] === rowValue && i[COL_FIELD] === colValue)
        .map((i) => ({ ...i, customer_name: file.customer_name, customer_data: file }))
    );

    const miscInsights = (file.miscellaneous_insights || [])
      .filter((i) => i[ROW_FIELD] === rowValue && i[COL_FIELD] === colValue)
      .map((i) => ({ ...i, customer_name: file.customer_name, customer_data: file }));

    return [...jobInsights, ...miscInsights];
  });

  if (!matching.length) return { average: null, count: 0, score: 0, insights: [] };

  const total = matching.reduce(
    (acc, i) => acc + (Number(i.insight_sentiment_score) || 0),
    0
  );
  const average = total / matching.length;           // average insight_sentiment_score (0..10)
  const count = matching.length;                     // occurrences
  const score = computeInsightScore({ averageSentiment: average, occurrences: count });

  return { average, count, score, insights: matching };
};

// Light blue -> bright orange gradient by SCORE (low→high)
// start: rgb(210,230,255)   end: rgb(255,140,0)
const colorByScore = (score, minScore, maxScore) => {
  if (!score || maxScore === minScore) return '#DADDE2'; // neutral gray if no variance
  const t = (score - minScore) / Math.max(1, (maxScore - minScore)); // normalize 0..1
  const start = { r: 210, g: 230, b: 255 };
  const end   = { r: 255, g: 140, b:   0 };
  const r = Math.round(start.r + (end.r - start.r) * t);
  const g = Math.round(start.g + (end.g - start.g) * t);
  const b = Math.round(start.b + (end.b - start.b) * t);
  return `rgb(${r}, ${g}, ${b})`;
};

export default function InsightsHeatmaps() {
  const { filteredData, loading } = useContext(DataAndFilterContext);
  const [openModal, setOpenModal] = useState(false);
  const [selectedInsights, setSelectedInsights] = useState([]);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const safeData = Array.isArray(filteredData) ? filteredData : [];

  // Rows = unique product areas
  const rowValues = useMemo(() => {
    const vals = new Set();
    for (const file of safeData) {
      (file.jobs_to_be_done || []).forEach((j) =>
        (j.insights || []).forEach((i) => i[ROW_FIELD] && vals.add(i[ROW_FIELD]))
      );
      (file.miscellaneous_insights || []).forEach((i) => i[ROW_FIELD] && vals.add(i[ROW_FIELD]));
    }
    return Array.from(vals);
  }, [safeData]);

  // Cols = unique overall sentiment fields (whatever the data has)
  const colValues = useMemo(() => {
    const vals = new Set();
    for (const file of safeData) {
      (file.jobs_to_be_done || []).forEach((j) =>
        (j.insights || []).forEach((i) => i[COL_FIELD] && vals.add(i[COL_FIELD]))
      );
      (file.miscellaneous_insights || []).forEach((i) => i[COL_FIELD] && vals.add(i[COL_FIELD]));
    }
    const present = Array.from(vals);
    const desired = ['Positive', 'Negative', 'Wish List'];
    return desired.filter((x) => present.includes(x)).concat(present.filter((x) => !desired.includes(x)));
  }, [safeData]);

  // Build a grid cache and compute score range for color scaling
  const { cellMap, minScore, maxScore } = useMemo(() => {
    const map = new Map(); // key `${row}__${col}` -> { average, count, score, insights }
    let min = Infinity;
    let max = -Infinity;

    for (const r of rowValues) {
      for (const c of colValues) {
        const cell = collectAvgCountScore(safeData, r, c);
        map.set(`${r}__${c}`, cell);
        if (cell.count > 0) {
          min = Math.min(min, cell.score);
          max = Math.max(max, cell.score);
        }
      }
    }
    if (min === Infinity) min = 0;
    if (max === -Infinity) max = 0;
    return { cellMap: map, minScore: min, maxScore: max };
  }, [safeData, rowValues, colValues]);

  const handleCellClick = (insights) => {
    if (insights.length > 0) {
      setSelectedInsights(insights);
      setOpenModal(true);
    }
  };
  const handleClose = () => setOpenModal(false);
  const handleCustomerClick = (customer) => { setSelectedCustomer(customer); setCustomerModalOpen(true); };
  const handleCustomerModalClose = () => setCustomerModalOpen(false);

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" align="center">Insight Scores by Product Area × Sentiment Intensity</Typography>
        <Typography variant="body1" align="center">Loading data...</Typography>
      </Box>
    );
  }

  if (!safeData.length) {
    return (
      <Box>
        <Typography variant="h6" align="center">Insight Scores by Product Area × Sentiment Intensity</Typography>
        <Typography variant="body1" align="center">No data available to display the heatmap.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" align="center" gutterBottom>
        Insight Scores by Product Area × Sentiment Intensity
      </Typography>
      <br />

      {/* Header Row for Columns */}
      <Grid container>
        <Grid item xs={2}></Grid>
        {colValues.map((col, index) => (
          <Grid item xs key={`header-${index}`}>
            <Typography variant="subtitle2" align="center">{col}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Data Rows */}
      {rowValues.map((row, rIdx) => (
        <Grid container key={`row-${rIdx}`}>
          <Grid item xs={2}>
            <Typography variant="subtitle2">{row}</Typography>
          </Grid>
          {colValues.map((col, cIdx) => {
            const { average, count, score, insights } =
              cellMap.get(`${row}__${col}`) || { average: null, count: 0, score: 0, insights: [] };
            const bg = colorByScore(score, minScore, maxScore);

            return (
              <Grid
                item
                xs
                key={`cell-${rIdx}-${cIdx}`}
                style={{
                  backgroundColor: bg,
                  border: '1px solid #ccc',
                  padding: '8px',
                  textAlign: 'center',
                  cursor: insights.length > 0 ? 'pointer' : 'default',
                }}
                onClick={() => handleCellClick(insights)}
              >
                {count > 0 ? (
                  <>
                    {/* Primary number: Insight Score (rounded for readability) */}
                    <Typography variant="body1" style={{ fontWeight: 700 }}>
                      Insight score: {Math.round(score)}
                    </Typography>
                    {/* Subtext: average sentiment & occurrences */}
                    <Typography variant="caption" style={{ fontSize: '0.75rem' }}>
                      Sentiment intensity {average.toFixed(1)} · {count} {count === 1 ? 'occurrence' : 'occurrences'}
                    </Typography>
                  </>
                ) : (
                  'n/a'
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}

      {/* Modal to show insights details */}
      <Modal open={openModal} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%', bgcolor: 'background.paper', boxShadow: 24, p: 4,
            maxHeight: '80vh', overflowY: 'auto',
          }}
        >
          <Typography variant="h6">Insight Details</Typography>
          {selectedInsights.map((insight, i) => (
            <Paper key={i} sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                "{insight.insight_quote}"
              </Typography>
              <Link
                href="#"
                variant="body2"
                underline="hover"
                sx={{ color: 'secondary.main', display: 'block', mb: 1 }}
              >
                Timestamps:&nbsp;
                {Array.isArray(insight.insight_timestamps)
                  ? insight.insight_timestamps.join(', ')
                  : insight.insight_timestamps}
              </Link>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Summary: {insight.insight_summary}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Product Area: {insight.insight_product_area}</Typography>
                <Typography variant="body2">Category: {insight.insight_category}</Typography>
                <Typography variant="body2">Type: {insight.insight_type}</Typography>
                <Typography variant="body2">Overall: {insight.insight_sentiment_overall}</Typography>
                <Typography variant="body2">Expressed: {insight.insight_sentiment_expressed}</Typography>
                <Typography variant="body2">
                  Sentiment Score: {Number(insight.insight_sentiment_score)?.toFixed(1)}
                </Typography>
                <Typography variant="body2">Justification: {insight.insight_sentiment_justification}</Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', color: 'primary.main', mt: 2 }}
                onClick={() => handleCustomerClick(insight.customer_data)}
              >
                Customer: {insight.customer_name}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Modal>

      {/* Customer Modal */}
      <CustomerModal open={customerModalOpen} onClose={handleCustomerModalClose} customer={selectedCustomer} />
    </Box>
  );
}
